import * as React from "react";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DateRangePickerProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Extra classes for the portaled calendar popover (e.g. page-scoped dark theming) */
  portalClassName?: string;
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Select date range",
  className,
  disabled = false,
  portalClassName,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setOpen(false);
  };

  const handleSelect = (range: DateRange | undefined) => {
    onChange(range);
    if (range?.from && range?.to) {
      setOpen(false);
    }
  };

  return (
    <div className={cn("grid gap-2 min-w-0", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative flex items-center min-w-0">
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal h-10 min-w-0",
                !value && "text-muted-foreground",
                value && "pr-8"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">
                {value?.from ? (
                  value.to ? (
                    <>
                      {format(value.from, "MM/dd/yy")} - {format(value.to, "MM/dd/yy")}
                    </>
                  ) : (
                    format(value.from, "MM/dd/yy")
                  )
                ) : (
                  placeholder
                )}
              </span>
            </Button>
          </PopoverTrigger>
          {value && (
            <button
              type="button"
              className="absolute right-2 h-4 w-4 opacity-50 hover:opacity-100"
              onClick={handleClear}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <PopoverContent className={cn("w-auto p-0", portalClassName)} align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
